import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './SpecEditor.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://54.165.139.51:9720';

/**
 * Visual spec editor with:
 * - Toggle between rendered preview and editable textarea
 * - Inline editing of tasks, risks, code_map
 * - "Generate from text" button that calls the backend to produce a spec from raw text
 *
 * Props:
 *   session: the full SpecSession object (with markdown_draft, task_breakdown, etc.)
 *   onUpdate: (updatedFields) => void — called when the user edits something locally
 *   userId: int
 */
const SAVE_DEBOUNCE_MS = 1200;

function SpecEditor({ session, onUpdate, userId }) {
  const [editMode, setEditMode] = useState(false);
  const [markdown, setMarkdown] = useState(session.markdown_draft || '');
  const [tasks, setTasks] = useState(session.task_breakdown || []);
  const [risks, setRisks] = useState(session.risks || []);
  const [redCases, setRedCases] = useState(session.red_cases || []);
  const [codeMap, setCodeMap] = useState(session.code_map || []);

  // Generate from scratch
  const [generateInput, setGenerateInput] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [showGenerate, setShowGenerate] = useState(!session.markdown_draft);

  // --- Autosave: debounce PUT /draft so edits survive a reload/approve ---
  // without firing a request on every keystroke. `pendingRef` accumulates
  // fields changed since the last successful save (merged, not replaced,
  // so a markdown edit followed quickly by a task edit saves both).
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const pendingRef = useRef({});
  const saveTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const flushSave = async () => {
    const fields = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(fields).length === 0) return;

    setSaveStatus('saving');
    try {
      const res = await fetch(`${API_URL}/api/specs/${session.id}/draft?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
        credentials: 'include',
      });
      if (res.ok) {
        setSaveStatus('saved');
      } else {
        setSaveStatus('error');
      }
    } catch (err) {
      setSaveStatus('error');
    }
  };

  // Notifies the parent immediately (so e.g. review state resets right
  // away) and schedules a debounced persist to the backend so /approve
  // always commits what's actually on screen, not a stale server copy.
  const notifyChange = (fields) => {
    onUpdate(fields);
    pendingRef.current = { ...pendingRef.current, ...fields };
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(flushSave, SAVE_DEBOUNCE_MS);
  };

  const handleMarkdownChange = (e) => {
    setMarkdown(e.target.value);
    notifyChange({ markdown_draft: e.target.value });
  };

  // --- Tasks ---
  const updateTask = (index, field, value) => {
    const updated = tasks.map((t, i) => (i === index ? { ...t, [field]: value } : t));
    setTasks(updated);
    notifyChange({ task_breakdown: updated });
  };

  const removeTask = (index) => {
    const updated = tasks.filter((_, i) => i !== index);
    setTasks(updated);
    notifyChange({ task_breakdown: updated });
  };

  const addTask = () => {
    const updated = [...tasks, { title: '', description: '' }];
    setTasks(updated);
    notifyChange({ task_breakdown: updated });
  };

  // --- Risks ---
  const updateRisk = (index, value) => {
    const updated = risks.map((r, i) => (i === index ? value : r));
    setRisks(updated);
    notifyChange({ risks: updated });
  };

  const removeRisk = (index) => {
    const updated = risks.filter((_, i) => i !== index);
    setRisks(updated);
    notifyChange({ risks: updated });
  };

  const addRisk = () => {
    const updated = [...risks, ''];
    setRisks(updated);
    notifyChange({ risks: updated });
  };

  // --- Red Cases ---
  const updateRedCase = (index, value) => {
    const updated = redCases.map((r, i) => (i === index ? value : r));
    setRedCases(updated);
    notifyChange({ red_cases: updated });
  };

  const removeRedCase = (index) => {
    const updated = redCases.filter((_, i) => i !== index);
    setRedCases(updated);
    notifyChange({ red_cases: updated });
  };

  const addRedCase = () => {
    const updated = [...redCases, ''];
    setRedCases(updated);
    notifyChange({ red_cases: updated });
  };

  // --- Code Map ---
  const updateCodeMapEntry = (index, field, value) => {
    const updated = codeMap.map((c, i) => (i === index ? { ...c, [field]: value } : c));
    setCodeMap(updated);
    notifyChange({ code_map: updated });
  };

  const removeCodeMapEntry = (index) => {
    const updated = codeMap.filter((_, i) => i !== index);
    setCodeMap(updated);
    notifyChange({ code_map: updated });
  };

  const addCodeMapEntry = () => {
    const updated = [...codeMap, { path: '', reason: '' }];
    setCodeMap(updated);
    notifyChange({ code_map: updated });
  };

  // --- Generate spec from raw text ---
  const handleGenerate = async () => {
    if (!generateInput.trim()) return;
    setGenerating(true);
    setGenerateError(null);
    try {
      const res = await fetch(
        `${API_URL}/api/specs/${session.id}/draft?user_id=${userId}`,
        { method: 'POST', credentials: 'include' }
      );
      const data = await res.json();
      if (res.ok) {
        setMarkdown(data.markdown_draft || '');
        setTasks(data.task_breakdown || []);
        setRisks(data.risks || []);
        setRedCases(data.red_cases || []);
        setCodeMap(data.code_map || []);
        setShowGenerate(false);
        // /draft (POST) already persisted this server-side -- just sync
        // local state and the parent, no need to also queue a PUT save.
        onUpdate({
          markdown_draft: data.markdown_draft,
          task_breakdown: data.task_breakdown,
          risks: data.risks,
          red_cases: data.red_cases,
          code_map: data.code_map,
        });
      } else {
        setGenerateError(data.detail || 'Generation failed');
      }
    } catch (err) {
      setGenerateError('Network error');
    } finally {
      setGenerating(false);
    }
  };

  // Simple markdown section rendering (not a full parser — highlights
  // headings, lists, code blocks for visual distinction).
  const renderMarkdownPreview = (text) => {
    if (!text) return <p className="empty-hint">No spec content yet.</p>;
    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {text}
      </ReactMarkdown>
    );
  };

  return (
    <div className="spec-editor">
      {!showGenerate && markdown && (
        <div className={`save-status save-status-${saveStatus}`}>
          {saveStatus === 'saving' && 'Saving…'}
          {saveStatus === 'saved' && 'Saved'}
          {saveStatus === 'error' && 'Failed to save — changes are local only'}
        </div>
      )}

      {/* Generate from text (shown when no draft yet or user wants to regenerate) */}
      {showGenerate && (
        <div className="generate-section">
          <h3>Generate spec from text</h3>
          <p className="generate-hint">
            Paste your requirement or notes below. Forge will clarify if needed, then generate a
            structured spec with tasks, risks, and code map.
          </p>
          <textarea
            className="generate-input"
            value={generateInput}
            onChange={(e) => setGenerateInput(e.target.value)}
            placeholder="Describe what you want built..."
            rows={6}
          />
          {generateError && <div className="message error">{generateError}</div>}
          <button
            type="button"
            className="btn-generate"
            onClick={handleGenerate}
            disabled={generating || !generateInput.trim()}
          >
            {generating ? 'Generating…' : 'Generate Spec'}
          </button>
        </div>
      )}

      {/* Spec content (markdown) */}
      {markdown && (
        <div className="editor-section">
          <div className="section-header">
            <h3>Spec Content</h3>
            <button
              type="button"
              className="btn-toggle-edit"
              onClick={() => setEditMode(!editMode)}
            >
              {editMode ? 'Preview' : 'Edit'}
            </button>
            {!showGenerate && (
              <button
                type="button"
                className="btn-toggle-edit btn-regen"
                onClick={() => setShowGenerate(true)}
              >
                Regenerate
              </button>
            )}
          </div>

          {editMode ? (
            <textarea
              className="markdown-editor"
              value={markdown}
              onChange={handleMarkdownChange}
              rows={20}
            />
          ) : (
            <div className="markdown-rendered">
              {renderMarkdownPreview(markdown)}
            </div>
          )}
        </div>
      )}

      {/* Tasks */}
      {tasks.length > 0 && (
        <div className="editor-section">
          <div className="section-header">
            <h3>Tasks ({tasks.length})</h3>
            <button type="button" className="btn-add" onClick={addTask}>+ Add task</button>
          </div>
          <div className="task-editor-list">
            {tasks.map((t, i) => (
              <div key={i} className="task-editor-item">
                <div className="task-editor-header">
                  <span className="task-index">{i + 1}</span>
                  <input
                    type="text"
                    className="task-title-input"
                    value={t.title}
                    onChange={(e) => updateTask(i, 'title', e.target.value)}
                    placeholder="Task title"
                  />
                  <button type="button" className="btn-remove" onClick={() => removeTask(i)} title="Remove task">
                    ×
                  </button>
                </div>
                <textarea
                  className="task-desc-input"
                  value={t.description}
                  onChange={(e) => updateTask(i, 'description', e.target.value)}
                  placeholder="Definition of done for this task..."
                  rows={2}
                />
                {t.depends_on_task_index != null && (
                  <span className="depends-tag">Depends on task {t.depends_on_task_index + 1}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risks */}
      <div className="editor-section">
        <div className="section-header">
          <h3>Risks ({risks.length})</h3>
          <button type="button" className="btn-add" onClick={addRisk}>+ Add risk</button>
        </div>
        {risks.length > 0 && (
          <div className="editable-list">
            {risks.map((r, i) => (
              <div key={i} className="editable-list-item risk-item">
                <input
                  type="text"
                  value={r}
                  onChange={(e) => updateRisk(i, e.target.value)}
                  placeholder="Describe a specific risk..."
                />
                <button type="button" className="btn-remove" onClick={() => removeRisk(i)}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Red Cases */}
      <div className="editor-section">
        <div className="section-header">
          <h3>Red Cases ({redCases.length})</h3>
          <button type="button" className="btn-add" onClick={addRedCase}>+ Add red case</button>
        </div>
        {redCases.length > 0 && (
          <div className="editable-list">
            {redCases.map((r, i) => (
              <div key={i} className="editable-list-item red-case-item">
                <input
                  type="text"
                  value={r}
                  onChange={(e) => updateRedCase(i, e.target.value)}
                  placeholder="Action a worker agent must never take..."
                />
                <button type="button" className="btn-remove" onClick={() => removeRedCase(i)}>×</button>
              </div>
            ))}
          </div>
        )}
        {redCases.length === 0 && (
          <p className="empty-hint">No red cases flagged — the common case for most specs.</p>
        )}
      </div>

      {/* Code Map */}
      <div className="editor-section">
        <div className="section-header">
          <h3>Code Map ({codeMap.length})</h3>
          <button type="button" className="btn-add" onClick={addCodeMapEntry}>+ Add path</button>
        </div>
        {codeMap.length > 0 && (
          <div className="editable-list">
            {codeMap.map((c, i) => (
              <div key={i} className="editable-list-item code-map-item">
                <input
                  type="text"
                  className="code-map-path"
                  value={c.path}
                  onChange={(e) => updateCodeMapEntry(i, 'path', e.target.value)}
                  placeholder="path/to/file.py"
                />
                <input
                  type="text"
                  className="code-map-reason"
                  value={c.reason}
                  onChange={(e) => updateCodeMapEntry(i, 'reason', e.target.value)}
                  placeholder="Why this file matters"
                />
                {c.verified === false && <span className="code-map-flag unverified">not in repo</span>}
                {c.verified === true && <span className="code-map-flag verified">verified</span>}
                <button type="button" className="btn-remove" onClick={() => removeCodeMapEntry(i)}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SpecEditor;
